import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState, lazy, Suspense } from "react";
import { ArrowRight, Sparkles, Globe, Smartphone, Megaphone, ShoppingCart, Database, Briefcase, Boxes, Code2, PhoneOutgoing, Headphones } from "lucide-react";
import { COMPANY, SERVICES } from "../lib/constants";
import Reveal from "../components/Reveal";
import ClientsSection from "../components/ClientsSection";
import Testimonials from "../components/Testimonials";
import api, { resolveImageUrl } from "../lib/api";

const HeroScene3D = lazy(() => import("../components/HeroScene3D"));

const ICONS = { Globe, Smartphone, Megaphone, ShoppingCart, Database, Sparkles, Briefcase, Boxes, Code2, PhoneOutgoing, Headphones };

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, 240]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -160]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* 3D scene background */}
      <motion.div className="absolute inset-0" style={{ scale, opacity }}>
        <Suspense fallback={<div className="absolute inset-0 bg-[#09090B]" />}>
          <HeroScene3D />
        </Suspense>
        {/* Soft fade so the headline stays readable on top of the 3D */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#09090B]/30 via-transparent to-[#09090B] pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_0%,_#09090B_85%)] pointer-events-none opacity-70" />
      </motion.div>

      {/* Animated mesh / grid / beams */}
      <div className="absolute inset-0 grid-bg opacity-15 pointer-events-none" />
      <div className="absolute inset-0 beams opacity-40 pointer-events-none" />

      {/* Floating orbs */}
      <motion.div style={{ y: y1 }} className="orb orb-flame orb-drift w-[420px] h-[420px] -top-20 -left-20 pointer-events-none opacity-60" />
      <motion.div style={{ y: y2 }} className="orb orb-azure orb-drift-2 w-[480px] h-[480px] -bottom-32 -right-24 pointer-events-none opacity-60" />

      {/* Content */}
      <motion.div style={{ opacity }} className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center z-10">
        <Reveal delay={0.15}>
          <h1 className="text-5xl sm:text-6xl lg:text-[88px] font-bold tracking-tight leading-[1.02]">
            <span className="block">Empowering the future</span>
            <span className="block">with <span className="fb-text">Technology</span></span>
            <span className="block">& <span className="azure-text">Education</span></span>
          </h1>
        </Reveal>

        <Reveal delay={0.35}>
          <p className="mt-8 text-lg text-zinc-300 max-w-2xl mx-auto leading-relaxed">
            We bridge the gap between learning and real-world implementation through innovative IT solutions
            and comprehensive tech education — built in Kolkata, delivered worldwide.
          </p>
        </Reveal>

        <Reveal delay={0.5}>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="hero-cta-start"
              className="group relative inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-[#FF6B1A] via-[#FFAA33] to-[#3B82F6] text-black font-semibold hover:shadow-[0_0_40px_rgba(255,107,26,0.6)] transition-all overflow-hidden"
            >
              <span className="relative z-10 flex items-center gap-2">Start Your Project <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" /></span>
              <span className="absolute inset-0 shine" />
            </Link>
            <Link
              to="/products"
              data-testid="hero-cta-products"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-white/20 text-white font-medium hover:bg-white/5 hover:border-[#3B82F6]/60 transition-all"
            >
              Explore Products
            </Link>
          </div>
        </Reveal>

        {/* Stats card */}
        <Reveal delay={0.7}>
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              { n: "50+", l: "Active Clients", c: "from-[#FF6B1A] to-[#FFAA33]" },
              { n: "20+", l: "Team Members", c: "from-[#3B82F6] to-[#60A5FA]" },
              { n: "11", l: "Service Verticals", c: "from-[#FFAA33] to-[#FF6B1A]" },
              { n: "100%", l: "On-time Delivery", c: "from-[#60A5FA] to-[#3B82F6]" },
            ].map((s, i) => (
              <motion.div
                key={s.l}
                whileHover={{ y: -6 }}
                className="glass rounded-2xl p-6 text-center relative overflow-hidden group"
              >
                <div className={`text-4xl font-bold bg-gradient-to-r ${s.c} bg-clip-text text-transparent`}>{s.n}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-2">{s.l}</div>
                <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B1A]/0 via-[#3B82F6]/0 to-[#FF6B1A]/0 group-hover:from-[#FF6B1A]/10 group-hover:to-[#3B82F6]/10 transition-all" />
              </motion.div>
            ))}
          </div>
        </Reveal>

        {/* Scroll cue */}
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="hidden md:flex absolute bottom-6 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-[10px] tracking-[0.3em] text-zinc-500 uppercase"
        >
          <span>Scroll</span>
          <span className="w-px h-10 bg-gradient-to-b from-[#FF6B1A] to-transparent" />
        </motion.div>
      </motion.div>
    </section>
  );
}

function HoneycombHex({ service, index }) {
  const Icon = ICONS[service.icon] || Sparkles;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, rotate: -6 }}
      whileInView={{ opacity: 1, y: 0, rotate: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.65, delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
      className="relative group"
    >
      <Link to={`/services/${service.key}`} className="block" data-testid={`hex-service-${service.key}`} aria-label={`Open ${service.title} details`}>
        <div className="hex-3d cursor-pointer">
          {/* Photo background */}
          <div className="hex-photo absolute inset-0 overflow-hidden">
            <img
              src={service.image}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover opacity-35 group-hover:opacity-90 group-hover:scale-110 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0c0c0e]/85 via-[#0c0c0e]/40 to-[#0c0c0e]/95 group-hover:from-[#FF6B1A]/30 group-hover:via-[#0c0c0e]/35 group-hover:to-[#3B82F6]/40 transition-all duration-700" />
            <div className="absolute -inset-1 bg-gradient-to-br from-[#FF6B1A]/0 via-transparent to-[#3B82F6]/0 group-hover:from-[#FF6B1A]/35 group-hover:to-[#3B82F6]/35 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
          </div>

          <div className="hex-content relative z-10">
            <div className="w-12 h-12 mb-3 rounded-2xl bg-gradient-to-br from-[#FF6B1A]/30 via-[#FFAA33]/15 to-[#3B82F6]/30 border border-white/20 flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-transform backdrop-blur-md">
              <Icon size={22} className="text-[#FFAA33] group-hover:text-white transition-colors" />
            </div>
            <h3 className="text-sm font-semibold text-white leading-tight drop-shadow-lg">{service.title}</h3>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#FFAA33] opacity-0 group-hover:opacity-100 transition-opacity mt-2 inline-flex items-center gap-1">View <ArrowRight size={10} /></p>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

function HoneycombGrid() {
  // Layout: row 0 = 4 hexes, row 1 = 3 (offset), row 2 = 4 — total 11
  const r1 = SERVICES.slice(0, 4);
  const r2 = SERVICES.slice(4, 7);
  const r3 = SERVICES.slice(7, 11);
  return (
    <div className="flex flex-col items-center gap-y-3" data-testid="services-honeycomb">
      <div className="flex justify-center gap-x-3">
        {r1.map((s, i) => <HoneycombHex key={s.key} service={s} index={i} />)}
      </div>
      <div className="flex justify-center gap-x-3 -my-12">
        {r2.map((s, i) => <HoneycombHex key={s.key} service={s} index={i + 4} />)}
      </div>
      <div className="flex justify-center gap-x-3">
        {r3.map((s, i) => <HoneycombHex key={s.key} service={s} index={i + 7} />)}
      </div>
    </div>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  const [collabs, setCollabs] = useState([]);
  const [eduPartners, setEduPartners] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data || [])).catch(() => {});
    api.get("/collaborations").then((r) => setCollabs(r.data || [])).catch(() => {});
    api.get("/education-partners").then((r) => setEduPartners(r.data || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Hero />

      {/* CLIENT MARQUEE */}
      <section className="py-12 border-y border-white/5 bg-[#0a0a0c] overflow-hidden relative">
        <p className="text-center text-xs uppercase tracking-[0.3em] text-zinc-500 mb-6">Trusted across industries</p>
        <div className="flex marquee-track gap-12 whitespace-nowrap">
          {[0, 1].flatMap((ix) =>
            ["Kolkata Municipal Corp.", "AusRetail Australia", "MediCare CRM", "EduSpark", "FinChain", "AgroBharat", "Codeflare Academy", "CityHosp"].map((c, i) => (
              <span key={`${ix}-${c}`} className={`text-2xl font-bold ${i % 2 === 0 ? "text-zinc-500" : "text-zinc-600"} hover:text-[#FF6B1A] transition-colors`}>{c} <span className="text-zinc-700 mx-4">·</span></span>
            ))
          )}
        </div>
      </section>

      {/* SERVICES HONEYCOMB */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 dot-bg opacity-30" />
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 orb orb-flame w-[500px] h-[500px] opacity-20" />
        <div className="absolute top-1/2 right-1/4 -translate-y-1/2 orb orb-azure w-[500px] h-[500px] opacity-20" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <Reveal><span className="text-xs font-bold tracking-[0.25em] text-[#FF6B1A] uppercase">What we do</span></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                <span className="text-white">11 services. </span>
                <span className="fb-text">One ecosystem.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-5 text-zinc-400 max-w-2xl mx-auto">From websites to AI automation, blockchain to telesales — Codeflare Labs is your end-to-end technology partner.</p>
            </Reveal>
          </div>

          <HoneycombGrid />

          <div className="mt-16 text-center">
            <Link to="/services" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 text-white hover:border-[#FF6B1A]/60 hover:bg-white/5 transition-all">
              See all services in detail <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <ClientsSection />

      {/* TRENDY FEATURE STRIP — auto-scrolling tech keywords */}
      <section className="py-12 border-y border-white/5 bg-[#0a0a0c] overflow-hidden relative">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-flame w-[300px] h-[300px] top-1/2 -left-24 -translate-y-1/2 opacity-30" />
          <div className="orb orb-azure w-[300px] h-[300px] top-1/2 -right-24 -translate-y-1/2 opacity-30" />
        </div>
        <div className="relative flex marquee-track gap-10 whitespace-nowrap text-3xl sm:text-5xl font-bold tracking-tight">
          {[0, 1].flatMap((row) =>
            ["React", "Next.js", "FastAPI", "Flutter", "Postgres", "MongoDB", "Stripe", "OpenAI", "Solidity", "AWS", "GCP", "Docker"].map((t, i) => (
              <span key={`${row}-${t}`} className={i % 3 === 0 ? "fb-text" : i % 3 === 1 ? "azure-text" : "text-zinc-700"}>
                {t} <span className="text-zinc-800 mx-3">/</span>
              </span>
            ))
          )}
        </div>
      </section>

      {/* PRODUCTS PREVIEW */}
      {products.length > 0 && (
        <section className="py-24 lg:py-32 relative overflow-hidden">
          <div className="absolute inset-0 mesh-bg opacity-90" />
          <div className="absolute -top-32 right-0 orb orb-azure w-[600px] h-[600px] opacity-30" />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
              <div>
                <Reveal><span className="text-xs font-bold tracking-[0.25em] text-[#3B82F6] uppercase">Our Products</span></Reveal>
                <Reveal delay={0.1}><h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold">Software we built. <span className="azure-text">Now for you.</span></h2></Reveal>
              </div>
              <Reveal delay={0.2}>
                <Link to="/products" className="inline-flex items-center gap-2 text-[#FF6B1A] font-medium">All products <ArrowRight size={16} /></Link>
              </Reveal>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {products.slice(0, 3).map((p, i) => (
                <Reveal key={p.id} delay={i * 0.07}>
                  <Link to={`/products/${p.slug}`} className="block group" data-testid={`home-product-${p.slug}`}>
                    <div className="tilt-card glass rounded-3xl overflow-hidden hover:border-[#3B82F6]/40 transition-all spotlight"
                      onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); e.currentTarget.style.setProperty('--x', `${e.clientX - r.left}px`); e.currentTarget.style.setProperty('--y', `${e.clientY - r.top}px`); }}>
                      <div className="aspect-video relative overflow-hidden">
                        {p.hero_image && <img src={resolveImageUrl(p.hero_image)} alt={p.name} className="w-full h-full object-cover opacity-60 group-hover:opacity-90 group-hover:scale-110 transition-all duration-700" />}
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                        <span className="absolute top-4 left-4 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-black/70 border border-white/15">{p.category}</span>
                      </div>
                      <div className="p-6">
                        <h3 className="text-lg font-semibold flex items-center gap-2">{p.name} <ArrowRight size={14} className="text-[#FF6B1A] opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" /></h3>
                        <p className="text-sm text-zinc-400 mt-2 line-clamp-2">{p.tagline}</p>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <Testimonials />

      {/* COLLABORATIONS */}
      {collabs.length > 0 && (
        <section className="py-20 lg:py-28 relative overflow-hidden border-t border-white/5" data-testid="home-collaborations">
          <div className="absolute -top-32 -left-24 orb orb-flame w-[400px] h-[400px] opacity-20" />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <Reveal><span className="text-xs font-bold tracking-[0.25em] text-[#FF6B1A] uppercase">Collaborations</span></Reveal>
              <Reveal delay={0.1}><h2 className="mt-4 text-4xl sm:text-5xl font-bold">Companies we <span className="fb-text">partner with</span>.</h2></Reveal>
              <Reveal delay={0.2}><p className="mt-4 text-zinc-400 max-w-xl mx-auto">Strategic alliances that help us deliver more value, faster.</p></Reveal>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {collabs.map((c, i) => (
                <Reveal key={c.id} delay={i * 0.04}>
                  <a href={c.website_url || "#"} target={c.website_url ? "_blank" : undefined} rel="noreferrer" data-testid={`collab-${c.id}`} className="block glass rounded-2xl p-6 h-full text-center hover:border-[#FF6B1A]/40 transition-all group">
                    {c.logo_url ? (
                      <img src={resolveImageUrl(c.logo_url)} alt={c.name} className="h-12 mx-auto object-contain opacity-80 group-hover:opacity-100 transition-opacity" />
                    ) : (
                      <div className="h-12 flex items-center justify-center text-xl font-bold text-zinc-300 group-hover:text-[#FF6B1A]">{c.name}</div>
                    )}
                    <p className="text-xs text-zinc-400 mt-3 line-clamp-2">{c.description}</p>
                  </a>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* EDUCATION PARTNERS */}
      {eduPartners.length > 0 && (
        <section className="py-20 lg:py-28 bg-[#0a0a0c] relative overflow-hidden border-t border-white/5" data-testid="home-edu-partners">
          <div className="absolute -bottom-32 -right-24 orb orb-azure w-[450px] h-[450px] opacity-20" />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <div>
                <Reveal><span className="text-xs font-bold tracking-[0.25em] text-[#3B82F6] uppercase">Education Partners</span></Reveal>
                <Reveal delay={0.1}><h2 className="mt-4 text-4xl sm:text-5xl font-bold">Colleges & universities <span className="azure-text">we work with</span>.</h2></Reveal>
              </div>
              <Reveal delay={0.2}><p className="text-zinc-400 max-w-md">From training programs to placement drives — partnering with India's top institutes to bridge classrooms and codebases.</p></Reveal>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {eduPartners.map((e, i) => (
                <Reveal key={e.id} delay={i * 0.05}>
                  <div className="glass rounded-2xl overflow-hidden h-full hover:border-[#3B82F6]/40 transition-all group" data-testid={`edu-partner-${e.id}`}>
                    {e.image_url && (
                      <div className="aspect-[16/9] overflow-hidden bg-[#1a1a1d]">
                        <img src={resolveImageUrl(e.image_url)} alt={e.name} className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
                      </div>
                    )}
                    <div className="p-5">
                      <div className="flex items-center gap-3">
                        {e.logo_url && <img src={resolveImageUrl(e.logo_url)} alt="" className="w-10 h-10 rounded-lg object-cover bg-white/5 border border-white/10" />}
                        <h3 className="font-semibold text-lg">{e.name}</h3>
                      </div>
                      {e.program && <p className="text-xs uppercase tracking-widest text-[#FFAA33] mt-3">{e.program}</p>}
                      {e.description && <p className="text-sm text-zinc-400 mt-2 line-clamp-3">{e.description}</p>}
                      {e.website_url && (
                        <a href={e.website_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#3B82F6] mt-3 hover:underline">Visit website <ArrowRight size={12} /></a>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}


    </div>
  );
}
